const CANONICAL = process.env.NEXT_PUBLIC_APP_URL || 'https://breakinbread.com';

/**
 * Turn any share target into a COMPLETE absolute URL. Callers often pass a bare
 * path like "/music/work_123"; sharing that verbatim produces a broken,
 * incomplete link. Resolve relative paths against the canonical origin and force
 * the host/protocol to the canonical domain.
 */
function toAbsolute(raw: string): string {
  if (!raw) return CANONICAL;
  try {
    const u = new URL(raw, CANONICAL);
    const c = new URL(CANONICAL);
    u.host = c.host;
    u.protocol = c.protocol;
    return u.toString();
  } catch {
    if (raw.startsWith('/')) return CANONICAL.replace(/\/$/, '') + raw;
    return raw;
  }
}

/** What's being shared. The optional card fields make a nicer card in chat. */
export interface ShareRequest {
  title: string;
  text?: string;
  url?: string;
  /** Share `url` exactly as given — for links to OTHER sites (a linked
   *  Spotify/Apple Music profile). Without it every host is rewritten to
   *  breakinbread.com, which is right for BB pages and wrong for those. */
  external?: boolean;
  imageUrl?: string;
  subtitle?: string;
  contentType?: 'track' | 'release' | 'artist' | 'playlist' | 'event' | 'video' | 'marketplace' | 'profile' | 'link' | 'page' | 'collab';
  contentId?: string;
  audioUrl?: string;
}

/** The full link to share. */
export function absoluteShareUrl(data: ShareRequest): string {
  return data.external && data.url
    ? data.url
    : toAbsolute(data.url || (typeof window !== 'undefined' ? window.location.href : ''));
}

/** The in-app path ("/music/abc?x=1#s2") for a BB link, '' for other sites. */
export function inAppPath(data: ShareRequest): string {
  if (data.external) return '';
  try {
    const u = new URL(absoluteShareUrl(data));
    return /(^|\.)breakinbread\.com$/.test(u.hostname) ? `${u.pathname}${u.search}${u.hash}` : '';
  } catch { return ''; }
}

/** A picture for the chat card when the caller gave none: the page's own share card. */
const CARD_TYPES: [RegExp, string][] = [
  [/^\/(music|releases)\/([^/?#]+)/, 'music'], [/^\/artists\/([^/?#]+)/, 'artist'], [/^\/videos\/([^/?#]+)/, 'video'],
  [/^\/marketplace\/([^/?#]+)/, 'item'], [/^\/news\/([^/?#]+)/, 'news'], [/^\/events\/([^/?#]+)/, 'event'],
  [/^\/playlists?\/([^/?#]+)/, 'playlist'], [/^\/labels\/([^/?#]+)/, 'label'], [/^\/profile\/([^/?#]+)/, 'user'],
];
export function shareCardImage(path: string): string {
  for (const [re, type] of CARD_TYPES) {
    const m = re.exec(path);
    if (m) return `/api/og/e/${type}/${encodeURIComponent(m[m.length - 1])}`;
  }
  return '';
}

/**
 * Share anything. In the app this opens the share panel (send to a BB member,
 * copy link, share via…, QR — components/share/share-sheet); without it, the
 * Web Share API with a clipboard fallback.
 */
export async function shareContent(data: ShareRequest): Promise<boolean> {
  if (typeof window !== 'undefined' && window.__bbShareSheet && !data.external) return window.__bbShareSheet(data);
  const shareUrl = data.external && data.url
    ? data.url
    : toAbsolute(data.url || (typeof window !== 'undefined' ? window.location.href : ''));
  const shareData = {
    title: data.title,
    text: data.text || data.title,
    url: shareUrl,
  };

  // Try Web Share API first (mobile + modern browsers)
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (err: any) {
      // User cancelled — not an error
      if (err?.name === 'AbortError') return false;
    }
  }

  // Fallback: copy link to clipboard
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } catch {
      // Final fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  }

  return false;
}
